import { Badge, Col, Row } from "reactstrap";
import { ComponentSpinner, LoaderOverlay } from "@components/spinner";
import {
    formatTimestamp,
    getPlural,
    getStatusBadge
} from "@src/utility/helpers/utils";

import Flex from "@components/ui-components/flex";
import LoadingButton from "@components/ui-components/loading-button";
import LongNameFormatter from "@components/formatters/long-name-formatter";
import { NoDataComponent } from "@components/no-data-component";
import classNames from "classnames";
import size from "lodash/size";
import useConversations from "./use-conversations";
import { useTranslation } from "react-i18next";

const Message = ({ message }) => {
    const isAdmin = message.author.type === "admin";
    const isBot = message.author.type === "bot";

    const messageBoxClasses = classNames("p-3 rounded w-100", {
        "bg-light-primary text-white" : isAdmin,
        "bg-light-info text-dark"     : isBot,
        "bg-light-secondary text-dark": !isAdmin && !isBot
    });

    const flexContainerClasses = classNames("d-flex flex-column", {
        "align-items-end"  : isAdmin,
        "align-items-start": !isAdmin
    });

    const iconWrapperClasses = classNames(
        "d-flex justify-content-center align-items-center rounded-circle",
        "bg-light",
        "text-center"
    );

    const iconClass = classNames("mdi", {
        "mdi-robot"  : isBot,
        "mdi-account": isAdmin,
        "mdi-message": !isAdmin && !isBot
    });

    return (
        <Flex className={`mb-3 ${isAdmin ? "flex-row-reverse" : "flex-row"}`}>
            <div className="flex-shrink-0">
                <div
                    className={classNames(iconWrapperClasses)}
                    style={{ width: "32px", height: "32px" }}>
                    <i
                        className={`${iconClass} fs-5 text-${isBot ? "info" : isAdmin ? "primary" : "secondary"}`}></i>
                </div>
            </div>

            <div className={flexContainerClasses} style={{ maxWidth: "80%" }}>
                <Flex align="center" className=" mb-1">
                    <span className="fw-medium small">
                        {message.author.name || message.author.email}
                    </span>
                    {message.created_at ? (
                        <span className="text-muted small">
                            {formatTimestamp(message.created_at)}
                        </span>
                    ) : null}
                </Flex>

                <div className={messageBoxClasses}>
                    <div
                        dangerouslySetInnerHTML={{ __html: message.body }}
                        className="p-1 break-words" />
                </div>
            </div>
        </Flex>
    );
};

function ConversationList ({ conversation }) {
    return (
        <div className="conversation-list">
            <Message message={conversation.source} />
            {size(conversation.replies) ? conversation.replies.map(reply => {
                return <Message key={reply.id} message={reply} />;
            }) : null}
        </div>
    );
}
const Conversations = props => {
    const {
        loading,
        getConversationDetails,
        detailsLoading,
        getConversations,
        convDetails,
        data: { conversations, active, nextBatch, count }
    } = useConversations(props);

    const { t } = useTranslation();
    return (
        <>
            <Flex justify="between" className="mb-1 border-bottom pb-1">
                <h4>
                    {t(getPlural({ text: "Conversation", length: count }))} ({count})
                </h4>
            </Flex>
            <div className="border rounded mt-1">
                <LoaderOverlay
                    active={loading}
                    children={
                        size(conversations) ? (
                            <Row className="match-height">
                                <Col
                                    lg={4}
                                    className="border-end overflow-scroll mb-25"
                                    style={{ height: "500px" }}>
                                    {size(conversations) ? conversations.map(
                                        conversation => {
                                            const contacts = size(
                                                conversation.contacts
                                            );
                                            return (
                                                <div
                                                    onClick={() => getConversationDetails(
                                                        conversation.id
                                                    )
                                                    }
                                                    className={classNames(
                                                        "p-1 cursor-pointer border-bottom",
                                                        {
                                                            "bg-light-primary":
                                                                conversation.id ===
                                                                active
                                                        }
                                                    )}>
                                                    <h6>
                                                        <i className="mdi mdi-email-outline me-1" />{" "}
                                                        {
                                                            conversation
                                                                .initiated_by
                                                                .name
                                                        }{" "}
                                                        {contacts ? `& ${contacts} more` : null}
                                                    </h6>
                                                    <LongNameFormatter
                                                        id={
                                                            conversation.id
                                                        }
                                                        max={50}
                                                        tag="h6"
                                                        className="fw-bold"
                                                        content={
                                                            conversation.title
                                                        } />
                                                </div>
                                            );
                                        }
                                    ) : null}
                                    <div className="px-2 mt-1 position-sticky bottom-0 bg-white z-1">
                                        <Flex
                                            justify="center"
                                            align="center">
                                            {nextBatch !== null ? (
                                                <LoadingButton
                                                    text="View More"
                                                    onClick={() => getConversations()
                                                    } />
                                            ) : null}
                                        </Flex>
                                    </div>
                                </Col>
                                <Col
                                    lg={8}
                                    className="overflow-scroll"
                                    style={{ height: "500px" }}>
                                    {detailsLoading ? (
                                        <ComponentSpinner />
                                    ) : size(convDetails) ? (
                                        <div className="p-75">
                                            <Flex
                                                justify="start"
                                                column>
                                                <Flex justify="between">
                                                    <LongNameFormatter
                                                        tag="h3"
                                                        className="fw-bold"
                                                        id={
                                                            convDetails.id
                                                        }
                                                        max={50}
                                                        content={
                                                            convDetails.title
                                                        } />
                                                    <Badge
                                                        color={
                                                            getStatusBadge(
                                                                convDetails.state
                                                            ).color
                                                        }>
                                                        {
                                                            getStatusBadge(
                                                                convDetails.state
                                                            ).title
                                                        }
                                                    </Badge>
                                                </Flex>
                                                <p>
                                                    {formatTimestamp(
                                                        convDetails.created_at
                                                    )}
                                                </p>
                                            </Flex>
                                            <ConversationList
                                                conversation={
                                                    convDetails
                                                } />
                                        </div>
                                    ) : null}
                                </Col>
                            </Row>
                        ) : (
                            <NoDataComponent />
                        )
                    } />
            </div>
        </>
    );
};
export default Conversations;
